import React, { useState } from "react";
import {
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Send,
  HelpCircle,
  MessageSquare,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from "@/config";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [
  {
    category: "General",
    question: "¿Qué es Mi Asistente Legal IA y cómo me ayuda?",
    answer:
      "Es una plataforma de inteligencia artificial especializada en derecho colombiano. Integra más de 1.8 millones de archivos de jurisprudencia de Altas Cortes, leyes y decretos para automatizar cobranza de cartera PH, realizar liquidaciones judiciales en 3 segundos, auditar contratos y fundamentar conceptos jurídicos con citas comprobables.",
  },
  {
    category: "Confiabilidad",
    question: "¿Cómo evitan que la IA alucine o cite normas derogadas?",
    answer:
      "A diferencia de modelos genéricos, utilizamos una arquitectura RAG (Retrieval-Augmented Generation) conectada a la base oficial de relatorías de la Corte Constitucional, CSJ y Consejo de Estado. Cada respuesta cita expresamente el radicado, la sentencia de unificación o el artículo vigente que la soporta.",
  },
  {
    category: "Cartera PH",
    question: "¿Cómo funciona la recuperación de cartera en Propiedad Horizontal?",
    answer:
      "La plataforma notifica a los propietarios en mora vía WhatsApp y correo con un botón de recaudo en línea PSE / Nequi. Si no hay acuerdo prejurídico, genera el Título Ejecutivo del Art. 48 de la Ley 675 en 1 solo clic y cumple estrictamente los horarios y frecuencias de la Ley 2300 de 2023.",
  },
  {
    category: "Calculadoras",
    question: "¿Las memorias de cálculo sirven como prueba judicial?",
    answer:
      "Sí. Todas las cuantificaciones (laboral Art. 64 CST, dosimetría penal por cuartos, perjuicios civiles indexados con IPC del DANE y fórmulas de sentencias del Consejo de Estado) exportan una memoria técnica detallada lista para anexar en demandas o contestaciones.",
  },
  {
    category: "Seguridad",
    question: "¿Mis datos y los expedientes de mis clientes están protegidos?",
    answer:
      "Absolutamente. Empleamos cifrado de extremo a extremo (AES-256), aislamiento de datos por despacho (arquitectura multi-tenant) y estricto cumplimiento del régimen colombiano de protección de datos personales (Habeas Data Ley 1581 de 2012).",
  },
  {
    category: "Planes",
    question: "¿Puedo probar la plataforma antes de suscribirme?",
    answer:
      "Sí, ofrecemos un período de prueba gratuito sin compromiso. Puedes registrarte de inmediato para probar las calculadoras, el asistente de investigación jurisprudencial y la auditoría de contratos.",
  },
  {
    category: "Educación & Clínicas",
    question: "¿Qué es MIA Campus y cómo funciona el Taller Socrático?",
    answer:
      "MIA Campus es el módulo pedagógico diseñado para facultades de derecho, semilleros y litigantes. Incluye el Taller Socrático interactivo que somete argumentos a contrapreguntas dialécticas y el Auditor de Citas Oficiales, el cual contrasta providencias contra las bases de datos de la Corte Constitucional, CSJ y Consejo de Estado para erradicar alucinaciones jurídicas.",
  },
  {
    category: "Consultorio Jurídico",
    question: "¿Cómo apoya la plataforma a los Consultorios Jurídicos bajo la Ley 2113 de 2021?",
    answer:
      "Automatiza el control preventivo de competencia por cuantía (hasta 50 SMLMV) y materias legales. Además, incorpora el flujo escalonado de revisión docente y la doble firma electrónica (estudiante y docente asesor) con estampado cronológico y hash SHA-256 de plena validez probatoria conforme a la Ley 527 de 1999.",
  },
];

export const FAQAndContactSection: React.FC = () => {
  const { toast } = useToast();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const validate = () => {
    const errors = { name: "", email: "", subject: "", message: "" };
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = "Ingrese su nombre completo.";
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = "Ingrese su correo electrónico.";
      isValid = false;
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) {
      errors.email = "Formato de correo no válido.";
      isValid = false;
    }

    if (!formData.subject.trim()) {
      errors.subject = "Por favor indique el motivo.";
      isValid = false;
    }

    if (!formData.message.trim() || formData.message.trim().length < 10) {
      errors.message = "El mensaje debe tener al menos 10 caracteres.";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      // Try posting to contact endpoint, fallback gracefully if local mock
      try {
        await fetch(`${API_BASE_URL}/api/contact/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
      } catch (err) {
        // Fallback for simulation
      }

      toast({
        title: "¡Mensaje recibido con éxito!",
        description: "Un asesor especializado se comunicará contigo en menos de 2 horas hábiles.",
      });

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast({
        title: "Error al enviar mensaje",
        description: "Por favor intenta de nuevo o escríbenos directamente por WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="faq" className="py-20 lg:py-24 bg-white dark:bg-slate-950 relative overflow-hidden">
      {/* Anchor for contact links */}
      <div id="contact" className="absolute top-1/2" />

      {/* Subtle background glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Heading */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100/80 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-900 text-legal-blue dark:text-blue-400 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
            <HelpCircle className="w-3.5 h-3.5 text-legal-blue dark:text-blue-400" />
            <span>Resolución de Inquietudes & Contacto Directo</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            Preguntas Frecuentes &{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-300">
              Atención Personalizada
            </span>
          </h2>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed">
            Todo lo que necesitas saber sobre la plataforma. Si tienes una consulta específica para tu firma o copropiedad, escríbenos directamente.
          </p>
        </div>

        {/* 2-Column Split: Left = FAQ Accordion, Right = Contact & Channels Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

          {/* Left Column: FAQ Accordion (7 cols) */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Dudas Comunes sobre el Servicio
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                {faqs.length} Respuestas Clave
              </span>
            </div>

            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={index}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen
                    ? "bg-slate-50/80 dark:bg-slate-900/80 border-blue-300 dark:border-blue-800 shadow-md shadow-blue-500/5"
                    : "bg-white dark:bg-slate-900/40 border-slate-200/80 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                    }`}
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-5 text-left flex items-start justify-between gap-4 select-none"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold transition-colors ${isOpen
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                          }`}
                      >
                        Q
                      </div>
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 block mb-1">
                          {faq.category}
                        </span>
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white leading-snug">
                          {faq.question}
                        </h3>
                      </div>
                    </div>

                    <div
                      className={`p-1.5 rounded-lg shrink-0 transition-transform duration-300 ${isOpen
                        ? "rotate-180 bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-400"
                        }`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 pl-14 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200/60 dark:border-slate-800/80 pt-3">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Quick reassurance badge */}
            <div className="p-4 rounded-2xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/60 dark:border-blue-900/60 flex items-center gap-3 text-xs text-blue-900 dark:text-blue-200 font-medium">
              <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <span>
                ¿Tienes una consulta específica o requieres integración API / CRM con tu software existente? Contáctanos a la derecha.
              </span>
            </div>
          </div>

          {/* Right Column: Contact Card & Quick Form (5 cols) */}
          <div className="lg:col-span-5">
            <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-900/5 relative">
              <div className="mb-6">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 block mb-1">
                  Respuesta Inmediata
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-tight mb-2">
                  Escríbenos o Solicita Asesoría
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Diligencia el formulario y te conectamos con un especialista en automatización jurídica.
                </p>
              </div>

              {/* Direct channels row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <div className="p-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="overflow-hidden">
                    <span className="text-[10px] text-slate-400 block font-semibold">Correo</span>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate block">
                      contacto@miasistente.com
                    </span>
                  </div>
                </div>

                <a
                  href="https://api.whatsapp.com/send?phone=573507679981&text=Hola%2C%20deseo%20informaci%C3%B3n%20y%20asesor%C3%ADa%20sobre%20Mi%20Asistente%20Legal%20IA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 flex items-center gap-2.5 hover:border-emerald-500/80 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-semibold">WhatsApp Oficial</span>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200 group-hover:text-emerald-600 block">
                      +57 350 767 9981
                    </span>
                  </div>
                </a>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="contact-name" className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 block">
                    Nombre Completo
                  </Label>
                  <Input
                    id="contact-name"
                    name="name"
                    placeholder="Ej. Dra. Laura Restrepo"
                    value={formData.name}
                    onChange={handleChange}
                    className="rounded-xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-sm h-11"
                  />
                  {formErrors.name && (
                    <p className="text-rose-500 text-xs mt-1">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact-email" className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 block">
                    Correo Electrónico
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="laura@bufeterestrepo.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="rounded-xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-sm h-11"
                  />
                  {formErrors.email && (
                    <p className="text-rose-500 text-xs mt-1">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact-subject" className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 block">
                    Solución de Interés
                  </Label>
                  <Input
                    id="contact-subject"
                    name="subject"
                    placeholder="Ej. Cartera PH / Calculadoras / Planes Despacho"
                    value={formData.subject}
                    onChange={handleChange}
                    className="rounded-xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-sm h-11"
                  />
                  {formErrors.subject && (
                    <p className="text-rose-500 text-xs mt-1">{formErrors.subject}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 block">
                    ¿Cómo podemos ayudarte?
                  </Label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    rows={3}
                    placeholder="Cuéntanos brevemente sobre tu despacho o copropiedad..."
                    value={formData.message}
                    onChange={handleChange}
                    className="rounded-xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-sm resize-none"
                  />
                  {formErrors.message && (
                    <p className="text-rose-500 text-xs mt-1">{formErrors.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-12 shadow-lg shadow-blue-600/20 hover:shadow-xl transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    "Enviando consulta..."
                  ) : (
                    <>
                      <span>Enviar Mensaje a Asesor</span>
                      <Send className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-slate-400 text-center leading-tight pt-1">
                  Tus datos están protegidos bajo la Ley 1581 de 2012 de Habeas Data. Sin spam.
                </p>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQAndContactSection;
